import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HardHat, Plus, RotateCcw, Repeat, Trash2 } from "lucide-react";
import EntityLink from "../components/EntityLink";
import { api, formatApiError } from "../lib/api";
import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import { useHasPermission } from "../contexts/PermissionsContext";
import { usePageAction } from "../contexts/UXChromeContext";
import { formatDate } from "../lib/domain";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import EmployeePicker from "../components/EmployeePicker";
import { Textarea } from "../components/ui/textarea";
import { TableSkeleton } from "../components/SkeletonHelpers";
import EmptyState from "../components/EmptyState";
import { rowOpenProps } from "../hooks/useRowOpen";
import { toast } from "sonner";
import DateInput from "../components/DateInput";

// ---------- Constants (mirror backend enums) ----------
export const ITEM_TYPES = [
    "safety_boots", "reflective_jacket", "overalls", "helmet", "gloves",
    "safety_glasses", "ear_protection", "gloves_chemical",
    "high_vis_vest", "hard_hat", "other",
];
export const CONDITIONS_AT_ISSUE = ["new", "used_good", "used_worn"];
export const STATUSES = ["issued", "returned", "replaced", "lost", "damaged"];
export const RETURN_CONDITIONS = ["good", "worn", "damaged", "unusable"];

const STATUS_TONE = {
    issued: "bg-emerald-50 text-emerald-700",
    returned: "bg-blue-50 text-blue-700",
    replaced: "bg-amber-50 text-amber-700",
    lost: "bg-rose-50 text-rose-700",
    damaged: "bg-rose-50 text-rose-700",
};

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pretty = (s) => (s || "").replace(/_/g, " ");
const today = () => new Date().toISOString().slice(0, 10);

export default function PPE() {
    const navigate = useNavigate();
    const { moduleCode: pathModuleCode } = useParams();
    const scopedId = useScopedModuleId();
    const canWrite = useHasPermission("ppe.write", scopedId || null);
    // Iter60.p50 — Return / Replace are the "signoff" actions,
    // gated on `ppe.signoff` (Attendance Officer STRIPPED). The
    // create / edit buttons still fire on `ppe.write`.
    const canSignoff = useHasPermission("ppe.signoff", scopedId || null);

    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("all");
    const [itemType, setItemType] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const [issueOpen, setIssueOpen] = useState(false);
    const [detail, setDetail] = useState(null); // full doc + replacement_chain
    const [returnFor, setReturnFor] = useState(null);
    const [replaceFor, setReplaceFor] = useState(null);
    const [deleteFor, setDeleteFor] = useState(null);

    usePageAction(canWrite ? { label: "Issue PPE", run: () => setIssueOpen(true) } : null, [canWrite]);

    const load = async () => {
        if (!scopedId) return;
        setLoading(true);
        try {
            const params = { module_id: scopedId };
            if (status !== "all") params.status = status;
            if (itemType !== "all") params.item_type = itemType;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const [r, e] = await Promise.all([
                api.get("/ppe/issuances", { params }),
                api.get("/employees", { params: { module_id: scopedId, page_size: 200 } }),
            ]);
            setItems(r.data.items || []);
            setEmployees(e.data.items || e.data || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [scopedId, status, itemType, dateFrom, dateTo]);

    const empMap = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

    const openDetail = async (row) => {
        try {
            const { data } = await api.get(`/ppe/issuances/${row.id}`);
            setDetail(data);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const doDelete = async (row) => {
        try {
            await api.delete(`/ppe/issuances/${row.id}`);
            toast.success("PPE issuance deleted");
            setDeleteFor(null);
            setDetail(null);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const totals = useMemo(() => {
        const t = { total: 0, spend: 0, issued: 0 };
        items.forEach((r) => {
            t.total += 1;
            t.spend += Number(r.total_cost || 0);
            if (r.status === "issued") t.issued += 1;
        });
        return t;
    }, [items]);

    return (
        <section className="space-y-4" data-testid="ppe-page">
            {/* KPI strip */}
            <div className="grid grid-cols-3 gap-3" data-testid="ppe-kpis">
                <KPI label="Records in view" value={totals.total} testid="ppe-kpi-count" />
                <KPI label="In circulation" value={totals.issued} testid="ppe-kpi-circ" />
                <KPI label="Spend in view" value={fmtUSD(totals.spend)} testid="ppe-kpi-spend" accent />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3" data-testid="ppe-filters">
                <div>
                    <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Status</div>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="h-9 w-40" data-testid="ppe-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Item type</div>
                    <Select value={itemType} onValueChange={setItemType}>
                        <SelectTrigger className="h-9 w-52" data-testid="ppe-type-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            {ITEM_TYPES.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">From</div>
                    <DateInput value={dateFrom} onChange={setDateFrom} className="h-9 w-40" data-testid="ppe-from" />
                </div>
                <div>
                    <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">To</div>
                    <DateInput value={dateTo} onChange={setDateTo} className="h-9 w-40" data-testid="ppe-to" />
                </div>
                <div className="ml-auto">
                    {canWrite && (
                        <Button className="asl-navy-bg text-white" onClick={() => setIssueOpen(true)} data-testid="ppe-issue-btn">
                            <Plus className="h-4 w-4 mr-1" /> Issue PPE
                        </Button>
                    )}
                </div>
            </div>

            <div className="asl-card overflow-hidden">
                {loading ? <TableSkeleton rows={5} cols={8} testid="ppe-loading" />
                    : items.length === 0 ? (
                        <EmptyState
                            icon={HardHat}
                            title="No PPE issuances"
                            description="Issue safety gear or uniforms to workers to start tracking cost and returns."
                            ctaLabel={canWrite ? "Issue PPE" : undefined}
                            onCta={canWrite ? () => setIssueOpen(true) : undefined}
                            testid="ppe-empty"
                        />
                    ) : (
                        <table className="asl-table asl-table--zebra" data-testid="ppe-table">
                            <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                style={{ background: "var(--asl-navy-50)" }}>
                                <tr>
                                    <th className="text-left px-3 py-2">Employee</th>
                                    <th className="text-left px-3 py-2">Item</th>
                                    <th className="text-left px-3 py-2">Size</th>
                                    <th className="text-right px-3 py-2">Qty</th>
                                    <th className="text-right px-3 py-2">Unit cost</th>
                                    <th className="text-right px-3 py-2">Total</th>
                                    <th className="text-left px-3 py-2">Issued</th>
                                    <th className="text-left px-3 py-2">Status</th>
                                    <th className="text-right px-3 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((r) => (
                                    <tr key={r.id}
                                        {...rowOpenProps(navigate, () => openDetail(r), { testid: `ppe-row-${r.id}`, className: "border-t" })}
                                        style={{ borderColor: "var(--asl-border)" }}>
                                        <td className="px-3 py-2">
                                            {r.employee_id ? (
                                                <EntityLink to={`/m/${pathModuleCode}/staff/${r.employee_id}`} testid={`ppe-emp-link-${r.id}`}>
                                                    {r.employee_name || empMap[r.employee_id]?.full_name || "—"}
                                                </EntityLink>
                                            ) : (r.employee_name || "—")}
                                        </td>
                                        <td className="px-3 py-2"><span className="text-xs asl-navy-text font-medium">{r.item_name}</span> <span className="text-[10px] text-[color:var(--asl-muted)] uppercase">· {pretty(r.item_type)}</span></td>
                                        <td className="px-3 py-2 text-xs">{r.size || "—"}</td>
                                        <td className="px-3 py-2 mono text-xs text-right">{r.quantity}</td>
                                        <td className="px-3 py-2 mono text-xs text-right">{fmtUSD(r.unit_cost)}</td>
                                        <td className="px-3 py-2 mono text-xs text-right asl-navy-text font-semibold">{fmtUSD(r.total_cost)}</td>
                                        <td className="px-3 py-2 mono text-xs">{formatDate(r.issue_date)}</td>
                                        <td className="px-3 py-2"><span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${STATUS_TONE[r.status] || "bg-gray-100"}`}>{r.status}</span></td>
                                        <td className="px-3 py-2 text-right space-x-1" data-no-row-click>
                                            {canSignoff && r.status === "issued" && (
                                                <>
                                                    <Button size="sm" variant="outline" onClick={() => setReturnFor(r)} data-testid={`ppe-return-${r.id}`}>
                                                        <RotateCcw className="h-3 w-3 mr-1" /> Return
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => setReplaceFor(r)} data-testid={`ppe-replace-${r.id}`}>
                                                        <Repeat className="h-3 w-3 mr-1" /> Replace
                                                    </Button>
                                                </>
                                            )}
                                            {canWrite && (
                                                <Button size="sm" variant="ghost" onClick={() => setDeleteFor(r)} data-testid={`ppe-delete-${r.id}`}>
                                                    <Trash2 className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </div>

            <IssueDialog open={issueOpen} onOpenChange={setIssueOpen} employees={employees} moduleId={scopedId} onSaved={load} />
            <DetailDialog row={detail} onClose={() => setDetail(null)} />
            <ReturnDialog row={returnFor} onClose={() => setReturnFor(null)} onSaved={load} />
            <ReplaceDialog row={replaceFor} employees={employees} moduleId={scopedId} onClose={() => setReplaceFor(null)} onSaved={load} />
            <DeleteConfirm row={deleteFor} onClose={() => setDeleteFor(null)} onConfirm={() => doDelete(deleteFor)} />
        </section>
    );
}

function KPI({ label, value, accent, testid }) {
    return (
        <div className="asl-card p-3" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <div className={`text-lg font-bold mono mt-1 ${accent ? "asl-crimson-text" : "asl-navy-text"}`}>{value}</div>
        </div>
    );
}

// ---------------- Issue Dialog ----------------
function IssueDialog({ open, onOpenChange, employees, moduleId, onSaved }) {
    const empty = () => ({
        employee_id: "", item_type: "safety_boots", item_name: "", size: "",
        quantity: 1, unit_cost: 0, issue_date: today(), condition_at_issue: "new", notes: "",
    });
    const [form, setForm] = useState(empty());
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (open) setForm(empty()); }, [open]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.employee_id) { toast.error("Pick an employee"); return; }
        setSaving(true);
        try {
            const payload = {
                ...form, module_id: moduleId,
                quantity: Number(form.quantity), unit_cost: Number(form.unit_cost),
                size: form.size || null, notes: form.notes || null,
            };
            await api.post("/ppe/issuances", payload);
            toast.success("PPE issued");
            onOpenChange(false);
            await onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const total = (Number(form.quantity) || 0) * (Number(form.unit_cost) || 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="ppe-issue-dialog">
                <DialogHeader><DialogTitle>Issue PPE / Clothing</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-1">
                    <div>
                        <Label className="text-xs uppercase">Employee</Label>
                        {/* Iter60.p15 / p15.1 — shared EmployeePicker (server-side scope). */}
                        <EmployeePicker
                            moduleId={moduleId}
                            employees={employees}
                            value={form.employee_id}
                            onChange={(id) => setForm({ ...form, employee_id: id })}
                            placeholder="Pick employee"
                            testid="ppe-issue-emp-picker"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Item type</Label>
                            <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                                <SelectTrigger data-testid="ppe-issue-type"><SelectValue /></SelectTrigger>
                                <SelectContent>{ITEM_TYPES.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Condition at issue</Label>
                            <Select value={form.condition_at_issue} onValueChange={(v) => setForm({ ...form, condition_at_issue: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{CONDITIONS_AT_ISSUE.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Item name / description</Label>
                        <Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                            required maxLength={200} data-testid="ppe-issue-name" placeholder="e.g. Steel-toe boots (brand/model)" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Size</Label>
                            <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}
                                maxLength={40} data-testid="ppe-issue-size" placeholder="Free-text" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Quantity</Label>
                            <Input type="number" min="1" value={form.quantity}
                                onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="ppe-issue-qty" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Unit cost</Label>
                            <Input type="number" min="0" step="0.01" value={form.unit_cost}
                                onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} data-testid="ppe-issue-cost" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Issue date</Label>
                            <DateInput value={form.issue_date}
                                onChange={(v) => setForm({ ...form, issue_date: v })} data-testid="ppe-issue-date" />
                        </div>
                        <div>
                            <div className="text-xs uppercase text-[color:var(--asl-muted)]">Total</div>
                            <div className="text-lg font-bold mono asl-navy-text mt-2" data-testid="ppe-issue-total">{fmtUSD(total)}</div>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Notes</Label>
                        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            maxLength={1000} data-testid="ppe-issue-notes" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" className="asl-navy-bg text-white" disabled={saving} data-testid="ppe-issue-submit">
                            {saving ? "Saving…" : "Issue"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ---------------- Return Dialog ----------------
function ReturnDialog({ row, onClose, onSaved }) {
    const empty = () => ({ returned_at: today(), returned_condition: "good", return_notes: "" });
    const [form, setForm] = useState(empty());
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (row) setForm(empty()); }, [row]);

    if (!row) return null;
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/ppe/issuances/${row.id}/return`, {
                returned_at: form.returned_at,
                returned_condition: form.returned_condition,
                return_notes: form.return_notes || null,
            });
            toast.success("Item marked returned");
            onClose();
            await onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="ppe-return-dialog">
                <DialogHeader><DialogTitle>Return: {row.item_name}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-1">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Returned on</Label>
                            <DateInput value={form.returned_at}
                                onChange={(v) => setForm({ ...form, returned_at: v })} data-testid="ppe-return-date" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Condition</Label>
                            <Select value={form.returned_condition} onValueChange={(v) => setForm({ ...form, returned_condition: v })}>
                                <SelectTrigger data-testid="ppe-return-cond"><SelectValue /></SelectTrigger>
                                <SelectContent>{RETURN_CONDITIONS.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Notes</Label>
                        <Textarea rows={3} value={form.return_notes}
                            onChange={(e) => setForm({ ...form, return_notes: e.target.value })} data-testid="ppe-return-notes" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" className="asl-navy-bg text-white" disabled={saving} data-testid="ppe-return-submit">
                            {saving ? "Saving…" : "Confirm Return"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ---------------- Replace Dialog ----------------
function ReplaceDialog({ row, employees, moduleId, onClose, onSaved }) {
    const empty = (r) => ({
        employee_id: r?.employee_id || "",
        item_type: r?.item_type || "safety_boots",
        item_name: r?.item_name || "",
        size: r?.size || "",
        quantity: r?.quantity || 1,
        unit_cost: r?.unit_cost ?? 0,
        issue_date: today(),
        condition_at_issue: "new",
        notes: "",
        returned_condition: "damaged",
        return_notes: "",
    });
    const [form, setForm] = useState(empty(row));
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (row) setForm(empty(row)); }, [row]);

    if (!row) return null;
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                new_item: {
                    module_id: moduleId,
                    employee_id: form.employee_id,
                    item_type: form.item_type,
                    item_name: form.item_name,
                    size: form.size || null,
                    quantity: Number(form.quantity),
                    unit_cost: Number(form.unit_cost),
                    issue_date: form.issue_date,
                    condition_at_issue: form.condition_at_issue,
                    notes: form.notes || null,
                },
                returned_condition: form.returned_condition,
                return_notes: form.return_notes || null,
            };
            await api.post(`/ppe/issuances/${row.id}/replace`, payload);
            toast.success("Replacement recorded");
            onClose();
            await onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const total = (Number(form.quantity) || 0) * (Number(form.unit_cost) || 0);

    return (
        <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="ppe-replace-dialog">
                <DialogHeader><DialogTitle>Replace: {row.item_name}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-1">
                    <div className="text-[11px] asl-crimson-text bg-rose-50 px-3 py-2 rounded">
                        The old issuance will be marked <b>replaced</b> using today's date. Provide the condition it was returned in.
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Old condition</Label>
                            <Select value={form.returned_condition} onValueChange={(v) => setForm({ ...form, returned_condition: v })}>
                                <SelectTrigger data-testid="ppe-replace-oldcond"><SelectValue /></SelectTrigger>
                                <SelectContent>{RETURN_CONDITIONS.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Old notes</Label>
                            <Input value={form.return_notes} onChange={(e) => setForm({ ...form, return_notes: e.target.value })} data-testid="ppe-replace-oldnotes" />
                        </div>
                    </div>
                    <hr />
                    <div className="text-xs asl-navy-text font-semibold">New issuance</div>
                    <div>
                        <Label className="text-xs uppercase">Employee</Label>
                        {/* Iter60.p15 / p15.1 — shared EmployeePicker (server-side scope). */}
                        <EmployeePicker
                            moduleId={moduleId}
                            employees={employees}
                            value={form.employee_id}
                            onChange={(id) => setForm({ ...form, employee_id: id })}
                            placeholder="Pick employee"
                            testid="ppe-replace-emp-picker"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Item type</Label>
                            <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{ITEM_TYPES.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Condition</Label>
                            <Select value={form.condition_at_issue} onValueChange={(v) => setForm({ ...form, condition_at_issue: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{CONDITIONS_AT_ISSUE.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Item name</Label>
                        <Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} required data-testid="ppe-replace-name" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Size</Label>
                            <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Quantity</Label>
                            <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Unit cost</Label>
                            <Input type="number" min="0" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Issue date</Label>
                            <DateInput value={form.issue_date} onChange={(v) => setForm({ ...form, issue_date: v })} />
                        </div>
                        <div>
                            <div className="text-xs uppercase text-[color:var(--asl-muted)]">New total</div>
                            <div className="text-lg font-bold mono asl-navy-text mt-2">{fmtUSD(total)}</div>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Notes on new item</Label>
                        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" className="asl-navy-bg text-white" disabled={saving} data-testid="ppe-replace-submit">
                            {saving ? "Saving…" : "Confirm Replacement"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ---------------- Detail Dialog ----------------
function DetailDialog({ row, onClose }) {
    if (!row) return null;
    const chain = row.replacement_chain || [];
    return (
        <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl" data-testid="ppe-detail-dialog">
                <DialogHeader>
                    <DialogTitle>{row.item_name} <span className="text-xs asl-crimson-text ml-2">{pretty(row.item_type)}</span></DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <Fact label="Employee" value={row.employee_name || row.employee_id} />
                    <Fact label="Module" value={row.module_name || row.module_id} />
                    <Fact label="Size" value={row.size || "—"} />
                    <Fact label="Quantity" value={row.quantity} />
                    <Fact label="Unit cost" value={fmtUSD(row.unit_cost)} />
                    <Fact label="Total cost" value={fmtUSD(row.total_cost)} accent />
                    <Fact label="Issue date" value={formatDate(row.issue_date)} />
                    <Fact label="Condition at issue" value={pretty(row.condition_at_issue)} />
                    <Fact label="Status" value={pretty(row.status)} />
                    {row.returned_at && <Fact label="Returned on" value={formatDate(row.returned_at)} />}
                    {row.returned_condition && <Fact label="Returned condition" value={pretty(row.returned_condition)} />}
                </div>
                {row.notes && (
                    <div className="mt-2 text-xs">
                        <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Notes</div>
                        <div className="asl-navy-text whitespace-pre-wrap">{row.notes}</div>
                    </div>
                )}
                {row.return_notes && (
                    <div className="mt-2 text-xs">
                        <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Return notes</div>
                        <div className="asl-navy-text whitespace-pre-wrap">{row.return_notes}</div>
                    </div>
                )}
                {chain.length > 0 && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase asl-navy-text font-semibold mb-2">Replacement history ({chain.length})</div>
                        <div className="space-y-1" data-testid="ppe-detail-chain">
                            {chain.map((c) => (
                                <div key={c.id} className="text-xs flex items-center justify-between border rounded px-2 py-1"
                                    style={{ borderColor: "var(--asl-border)" }}>
                                    <span className="mono">{formatDate(c.issue_date)}</span>
                                    <span>{c.item_name}</span>
                                    <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${STATUS_TONE[c.status] || "bg-gray-100"}`}>{c.status}</span>
                                    <span className="mono">{fmtUSD(c.total_cost)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Fact({ label, value, accent }) {
    return (
        <div>
            <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">{label}</div>
            <div className={`text-sm ${accent ? "asl-crimson-text font-semibold" : "asl-navy-text"}`}>{value}</div>
        </div>
    );
}

// ---------------- Delete Confirm ----------------
function DeleteConfirm({ row, onClose, onConfirm }) {
    if (!row) return null;
    return (
        <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm" data-testid="ppe-delete-dialog">
                <DialogHeader><DialogTitle>Delete PPE issuance?</DialogTitle></DialogHeader>
                <div className="text-sm">
                    This will soft-delete the record for <b>{row.item_name}</b>. It will be hidden from lists but can be recovered by an admin.
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-rose-600 text-white hover:bg-rose-700" onClick={onConfirm} data-testid="ppe-delete-confirm">Delete PPE record</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
